import "../../styles/conversation-drawer.css";
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import { useDispatch } from "react-redux";
import {
  addConversation,
  fetchAvailableModels,
} from "../../store/conversation.tsx";
import ConversationDrawerActions from "./ConversationDrawerActions.tsx";
import ConversationDrawerSection from "./ConversationDrawerSection.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import CloseDrawerIcon from "../icons/CloseDrawerIcon.tsx";
import OpenDrawerIcon from "../icons/OpenDrawerIcon.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { CONVERSATION_LAYOUT } from "./constants.ts";
import { Box, useMediaQuery } from "@mui/material";
import AddConversationIcon from "../icons/AddConversationIcon.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface AppBarProps extends MuiAppBarProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  onShowIntegrations?: () => void;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "block",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  zIndex: 2,
  backgroundColor: "inherit",
  flexShrink: 0,
}));

const ConversationDrawer: React.FC<AppBarProps> = (props) => {
  const theme = useTheme();
  const conversations = useTypedSelector(
    (state) => state.conversationState.conversations,
  );

  const dispatch = useDispatch();

  const isDesktopScreen: boolean = useMediaQuery(theme.breakpoints.up("lg"));

  const handleAddConversation = async () => {
    dispatch(addConversation() as any);
  };

  const handleDrawerOpen = () => {
    props.setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    props.setOpenDrawer(false);
  };

  React.useEffect(() => {
    // fetch available models
    dispatch(fetchAvailableModels() as any);
  }, []);

  return (
    <Box>
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: `${theme.palette.primary.main}`,
            color: `${theme.palette.common.black}`,
            display: "flex",
            flexDirection: "column",
            scrollbarColor: "#76573d #a97d58",
            overflowY: "auto",
            overflowX: "hidden",
            paddingLeft: "20px",
            paddingRight: "14px",
            paddingBottom: {
              xs: CONVERSATION_LAYOUT.bottomPadding.mobile,
              sm: CONVERSATION_LAYOUT.bottomPadding.tablet,
              md: CONVERSATION_LAYOUT.bottomPadding.desktop,
            }, // Responsive bottom padding using conversation constants
          },
        }}
        sx={{
          height: "100vh",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SETTINGS.drawerWidth,
            boxSizing: "border-box",
          },
          padding: 0,
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
        variant="persistent"
        anchor="left"
        open={props.openDrawer}
        id="conversation-drawer"
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: theme.palette.primary.main,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            paddingTop: "16px",
            paddingBottom: "6px",
            mx: "-10px", // keeps are expanded so covers scroll elements
            px: "10px",
          }}
        >
          <DrawerHeader
            sx={{
              minHeight: "50px !important",
              paddingTop: "14px",
              paddingBottom: "14px",
            }}
          >
            <IconButton
              onClick={handleDrawerClose}
              sx={{
                float: "left",
                fontSize: theme.typography.h5.fontSize,
                top: "auto",
                marginLeft: "-14px", // Compensate for IconButton padding
              }}
            >
              <CloseDrawerIcon color={theme.palette.common.black} />
            </IconButton>
            <IconButton
              onClick={() => handleAddConversation()}
              sx={{
                float: "right",
                fontSize: theme.typography.h5.fontSize,
                marginRight: "-12px", // Compensate for IconButton padding
              }}
            >
              <AddConversationIcon color={theme.palette.common.black} />
            </IconButton>
          </DrawerHeader>
          <ConversationDrawerActions
            closeDrawer={!isDesktopScreen ? handleDrawerClose : () => {}}
            onShowIntegrations={props.onShowIntegrations}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
          }}
          id="conversation-drawer-section-container"
        >
          <ConversationDrawerSection
            conversations={conversations.pinned}
            closeDrawer={!isDesktopScreen ? handleDrawerClose : () => {}}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations pinned by the user",
                message: "Pinned conversations",
              }),
            )}
            toggleConversation={false}
          />
          <ConversationDrawerSection
            conversations={conversations.last_7_days}
            closeDrawer={!isDesktopScreen ? handleDrawerClose : () => {}}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations  created in the last 7 days",
                message: "Last 7 days",
              }),
            )}
            toggleConversation={true}
          />
          <ConversationDrawerSection
            conversations={conversations.last_30_days}
            closeDrawer={!isDesktopScreen ? handleDrawerClose : () => {}}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations  created in the last 30 days",
                message: "Last 30 days",
              }),
            )}
            toggleConversation={true}
          />
          <ConversationDrawerSection
            conversations={conversations.other}
            closeDrawer={!isDesktopScreen ? handleDrawerClose : () => {}}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations older than 30 days",
                message: "Older",
              }),
            )}
            toggleConversation={true}
          />
        </Box>
      </Drawer>
      {isDesktopScreen && (
        <IconButton
          onClick={handleDrawerOpen}
          sx={{
            left: "18px", // Fine-tuned alignment
            top: `${SETTINGS.topMargin - 8}px`, // Adjust for button padding to align icon with content
            position: "absolute",
          }}
        >
          <OpenDrawerIcon
            color={theme.palette.secondary.main}
            fontSize={theme.typography.h5.fontSize}
          />
        </IconButton>
      )}
    </Box>
  );
};

export default ConversationDrawer;
